import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import { Send } from "lucide-react";
import { cn } from "../../../../utils/cn";

// Redactor del asistente interno: Enter envía, Shift+Enter hace salto de línea
// (mismo atajo que AIChatWidget). Bloqueado mientras hay un envío en vuelo o una
// respuesta pendiente: dos mensajes seguidos desordenarían el hilo, que es una
// conversación 1:1 con la IA.
export default function InternalComposer({
  value,
  onChange,
  onSend,
  sending,
  awaiting,
  maxLength,
}) {
  const { t } = useTranslation();
  const inputRef = useRef(null);

  const trimmed = value.trim();
  const disabled = sending || awaiting;
  const canSend = !!trimmed && !disabled;
  // El contador solo aparece cerca del tope: mostrarlo siempre convierte un
  // límite del backend en ruido permanente.
  const showCounter = value.length > maxLength * 0.9;

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      if (canSend) onSend();
    }
  };

  return (
    <div className="shrink-0 border-t border-border bg-bg px-4 sm:px-6 py-3">
      <div className="flex items-end gap-2">
        <div className="flex-1 min-w-0">
          <textarea
            ref={inputRef}
            value={value}
            onChange={(event) => onChange(event.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={
              awaiting
                ? t("Esperando la respuesta del asistente…")
                : t("Escribe tu consulta…")
            }
            rows={1}
            maxLength={maxLength}
            disabled={disabled}
            aria-label={t("Mensaje para el asistente interno")}
            className={cn(
              "w-full min-h-[44px] max-h-40 resize-none rounded-lg border border-border bg-bg px-3 py-2.5 text-sm text-fg",
              "placeholder:text-fg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500",
              "disabled:opacity-60 disabled:cursor-not-allowed",
            )}
          />
        </div>
        <button
          type="button"
          onClick={onSend}
          disabled={!canSend}
          aria-label={t("Enviar mensaje")}
          title={t("Enviar (Enter)")}
          className={cn(
            "shrink-0 inline-flex items-center justify-center w-11 h-11 rounded-lg transition-colors",
            "hd-brand-solid bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700",
            "disabled:opacity-50 disabled:cursor-not-allowed",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2",
          )}
        >
          {sending ? (
            <span
              className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin motion-reduce:animate-none"
              aria-hidden="true"
            />
          ) : (
            <Send className="w-4 h-4" aria-hidden="true" />
          )}
        </button>
      </div>

      <div className="mt-1.5 flex items-center justify-between gap-3">
        <p className="text-[11px] text-fg-subtle">
          {t("Enter envía · Shift+Enter salta de línea")}
        </p>
        {showCounter && (
          <p className="text-[11px] text-fg-subtle tabular-nums">
            {t("{{n}}/{{max}} caracteres")
              .replace("{{n}}", value.length)
              .replace("{{max}}", maxLength)}
          </p>
        )}
      </div>
    </div>
  );
}
